// src/pages/mairie/GestionTaxes.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionTaxes() {
    const [taxes, setTaxes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        nom: '',
        description: '',
        periode: 'annuelle'
    });

    const fetchTaxes = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/taxes.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) setTaxes(data.taxes);
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTaxes(); }, []);

    // ✅ Réinitialiser le formulaire
    const resetForm = () => {
        setFormData({
            nom: '',
            description: '',
            periode: 'annuelle'
        });
        setEditingId(null);
        setShowForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nom.trim()) {
            toast.error('Le nom est requis');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const payload = { ...formData };
            if (editingId) payload.id_taxe = editingId;

            const res = await fetch(`${config.apiBase}/mairie/taxes.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                toast.success(editingId ? 'Taxe modifiée' : 'Taxe ajoutée');
                resetForm(); // ✅ Réinitialiser après succès
                fetchTaxes();
            } else {
                toast.error(data.message || 'Erreur');
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleEdit = (taxe) => {
        setFormData({
            nom: taxe.nom,
            description: taxe.description || '',
            periode: taxe.periode
        });
        setEditingId(taxe.id_taxe);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette taxe ?')) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/taxes.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id_taxe: id })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Taxe supprimée');
                fetchTaxes();
            } else {
                toast.error(data.message || 'Erreur');
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const toggleForm = () => {
        if (showForm) {
            resetForm();
        } else {
            setShowForm(true);
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold">💰 Gestion des taxes</h1>
                    <p className="text-sm text-gray-500">Définissez les types de taxes</p>
                </div>
                <button
                    onClick={toggleForm}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    {showForm ? '✕ Annuler' : '+ Nouvelle taxe'}
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-lg border mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Nom *</label>
                            <input
                                type="text"
                                value={formData.nom}
                                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Période</label>
                            <select
                                value={formData.periode}
                                onChange={(e) => setFormData({ ...formData, periode: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                            >
                                <option value="annuelle">Annuelle</option>
                                <option value="mensuelle">Mensuelle</option>
                                <option value="journaliere">Journalière</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1">Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                rows="2"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <button 
                            type="submit" 
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                        >
                            {editingId ? '💾 Modifier' : '➕ Ajouter'}
                        </button>
                        <button 
                            type="button" 
                            onClick={resetForm} 
                            className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition"
                        >
                            Annuler
                        </button>
                    </div>
                </form>
            )}

            {loading && !showForm ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
                </div>
            ) : taxes.length === 0 ? (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                    <div className="text-6xl mb-4">📭</div>
                    <p className="text-gray-500 text-lg">Aucune taxe définie</p>
                    <p className="text-sm text-gray-400">Cliquez sur "Nouvelle taxe" pour en créer une</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {taxes.map(taxe => (
                        <div key={taxe.id_taxe} className="bg-white rounded-xl shadow-lg border p-5 hover:shadow-xl transition">
                            <h3 className="font-bold text-lg">{taxe.nom}</h3>
                            <span className={`inline-block text-xs px-2 py-1 rounded-full mt-1 ${
                                taxe.periode === 'annuelle' ? 'bg-blue-100 text-blue-700' :
                                taxe.periode === 'mensuelle' ? 'bg-green-100 text-green-700' :
                                'bg-orange-100 text-orange-700'
                            }`}>
                                {taxe.periode === 'annuelle' ? '📅 Annuelle' :
                                 taxe.periode === 'mensuelle' ? '📆 Mensuelle' : '📆 Journalière'}
                            </span>
                            {taxe.description && (
                                <p className="text-sm text-gray-500 mt-2 line-clamp-2">{taxe.description}</p>
                            )}
                            <div className="mt-4 flex gap-2">
                                <button 
                                    onClick={() => handleEdit(taxe)} 
                                    className="bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100 transition text-sm flex-1"
                                >
                                    ✏️ Modifier
                                </button>
                                <button 
                                    onClick={() => handleDelete(taxe.id_taxe)} 
                                    className="bg-red-50 text-red-600 px-3 py-1 rounded hover:bg-red-100 transition text-sm flex-1"
                                >
                                    🗑️ Supprimer
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Statistiques */}
            {taxes.length > 0 && (
                <div className="mt-6 bg-white rounded-xl shadow-lg p-4 border">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>📊 Total: <strong>{taxes.length}</strong> taxes définies</span>
                        <span>
                            📋 Périodes: 
                            {['annuelle', 'mensuelle', 'journaliere'].map(p => {
                                const count = taxes.filter(t => t.periode === p).length;
                                return count > 0 && (
                                    <span key={p} className="ml-2 px-2 py-1 bg-gray-100 rounded-full text-xs">
                                        {p === 'annuelle' ? 'Annuelle' :
                                         p === 'mensuelle' ? 'Mensuelle' : 'Journalière'}: {count}
                                    </span>
                                );
                            })}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}